import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import moment from 'moment';
import { Apollo } from 'apollo-angular';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { update_pricing_details } from './graphql/pricingDates';
import { update_project_dates } from '../deal-dates/graphql/dealDates';
import { FormControl } from '@angular/forms';
import { DataFormatService } from '../../../services/data-format.service';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subscription,
} from 'rxjs';
import { participants_search_people } from '../participants/graphql/participantsSearchPeople';
import { update_project_participants } from '../participants/graphql/editParticipants';
import { checkPermission } from '../../../pipes/permission.pipe';
import Permission from '../../../interfaces/permission';

@Component({
    selector: 'deal-pricing',
    templateUrl: './pricing.component.html',
    styleUrls: ['./pricing.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PricingComponent implements OnInit {
  @Input() dates: any;
  @Input() permissions: any;
  @Input() projectid: string;
  @Input() placementExpDateID: string;
  @Input() placementIssuedDateID: string;
  @Input() responseToDevID: string;
  @Input() actualDateClassID: string;
  @Input() pricingDetails: any;
  @Input() participants$: Observable<any>;
  @Input() roles: any;
  @Output() participantsChange: EventEmitter<any> = new EventEmitter();
  participants: any = [];
  participantsSub: Subscription;
  placementExpDateObj: any;
  placementExpDate: string;
  placementExpDateNew: string;
  placementExpDateNewFormatted: string;

  placementIssuedDateObj: any;
  placementIssuedDate: string;
  placementIssuedDateNew: string;
  placementIssuedDateNewFormatted: string;

  responseDateObj: any;
  responseDate: string;
  responseDateNew: string;
  responseDateNewFormatted: string;

  hasPricingPermissions: boolean = false;
  isEditMode: boolean = false;

  isReasoningEditMode: boolean = false;
  syndReasoning: FormControl;
  syndReasoningChanged: boolean = false;

  isQuestionEditMode: boolean = false;
  syndQuestions: FormControl;
  syndQuestionsChanged: boolean = false;

  isNonCRAEditMode: boolean = false;
  nonCRA: FormControl;
  nonCRAChanged: boolean = false;

  syndLeadRole: any;
  syndLeadPart: any;
  syndLeadPartNew: any;

  typeSubscription: Subscription = new Subscription();
  searchParticipant: FormControl = new FormControl('');
  searchParticipantSubs: any = [];
  searchParticipants: any = [];

  currentSearchResults: any = [];
  loadingSearchResults: Boolean = false;
  querySubscription: any;

  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    public dataFormat: DataFormatService
  ) {}

  ngOnInit() {
    this.participantsSub = this.participants$.subscribe((participants: any) => {
      this.participants = participants;
      this.formatSyndLeadPart();
    });
    this.syndReasoning = new FormControl(
      this.pricingDetails?.syndications_pricing_reasoning
        ? this.pricingDetails.syndications_pricing_reasoning
        : ''
    );
    this.syndQuestions = new FormControl(
      this.pricingDetails?.syndications_questions
        ? this.pricingDetails.syndications_questions
        : ''
    );
    this.nonCRA = new FormControl(
      this.pricingDetails?.non_cra_execution
        ? this.pricingDetails.non_cra_execution
        : ''
    );
    this.formatDates();

    for (let i = 0; i < this.permissions.groupsFlattened.length; i++) {
      if (
        checkPermission(
          (this.permissions.projectPricingDetailPermissions as unknown as
            | Permission
            | undefined) ?? null,
          '_edit'
        )
      ) {
        this.hasPricingPermissions = true;
        break;
      }
    }

    for (let i = 0; i < this.roles.length; i++) {
      if (this.roles[i].name === 'Syndications Lead') {
        this.syndLeadRole = this.roles[i];
        break;
      }
    }

    this.formatSyndLeadPart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('participants')) {
      this.formatSyndLeadPart();
    }
    if (changes.hasOwnProperty('dates')) {
      this.formatDates();
    }
  }

  ngOnDestroy() {
    this.participantsSub?.unsubscribe();
  }
  formatDates() {
    let foundPlacementExp: boolean = false;
    let foundPlacementIssued: boolean = false;
    let foundResponseDev: boolean = false;
    if (this.dates && this.dates.length > 0) {
      for (let i = 0; i < this.dates.length; i++) {
        if (this.dates[i].dateType.name === 'Placement Guidance Expiration') {
          foundPlacementExp = true;
          this.placementExpDateObj = this.dates[i];
        } else if (
          this.dates[i].dateType.name === 'Placement Guidance Issued'
        ) {
          foundPlacementIssued = true;
          this.placementIssuedDateObj = this.dates[i];
        } else if (
          this.dates[i].dateType.name === 'Response to Developer Needed'
        ) {
          foundResponseDev = true;
          this.responseDateObj = this.dates[i];
        }
      }
    }
    this.placementExpDate = foundPlacementExp
      ? moment(this.placementExpDateObj.date).toISOString()
      : '';
    this.placementIssuedDate = foundPlacementIssued
      ? moment(this.placementIssuedDateObj.date).toISOString()
      : '';
    this.responseDate = foundResponseDev
      ? moment(this.responseDateObj.date).toISOString()
      : '';

    this.placementExpDateNew = this.placementExpDate;
    this.placementExpDateNewFormatted = this.placementExpDateNew
      ? moment.utc(this.placementExpDateNew).format('MM/DD/YYYY')
      : '';
    this.placementIssuedDateNew = this.placementIssuedDate;
    this.placementIssuedDateNewFormatted = this.placementIssuedDateNew
      ? moment.utc(this.placementIssuedDateNew).format('MM/DD/YYYY')
      : '';
    this.responseDateNew = this.responseDate;
    this.responseDateNewFormatted = this.responseDateNew
      ? moment.utc(this.responseDateNew).format('MM/DD/YYYY')
      : '';
  }

  formatSyndLeadPart() {
    this.syndLeadPart = null;
    this.syndLeadPartNew = null;
    for (let i = 0; i < this.participants.length; i++) {
      if (this.participants[i].role?.name === 'Syndications Lead') {
        this.syndLeadPart = this.participants[i];
        this.syndLeadPartNew = this.syndLeadPart;
        this.searchParticipant.setValue(this.syndLeadPartNew.person.name);
        break;
      }
    }
    if (this.syndLeadPartNew === null) {
      this.searchParticipant.setValue('');
    }
  }

  toggleQuestionEditMode() {
    if (
      this.isQuestionEditMode &&
      this.syndQuestionsChanged &&
      this.hasPricingPermissions
    ) {
      let reasoningArr = {};
      if (this.pricingDetails) {
        reasoningArr = {
          id: this.pricingDetails.id,
          syndications_questions: this.syndQuestions.value,
        };
      } else {
        reasoningArr = {
          project_id: this.projectid,
          syndications_questions: this.syndQuestions.value,
        };
      }
      this.updatePricingDetails(
        reasoningArr,
        'Updated Syndications Questions',
        'questions'
      );
    }
    this.isQuestionEditMode = !this.isQuestionEditMode;
  }

  toggleReasoningEditMode() {
    if (
      this.isReasoningEditMode &&
      this.syndReasoningChanged &&
      this.hasPricingPermissions
    ) {
      let reasoningArr = {};
      if (this.pricingDetails) {
        reasoningArr = {
          id: this.pricingDetails.id,
          syndications_pricing_reasoning: this.syndReasoning.value,
        };
      } else {
        reasoningArr = {
          project_id: this.projectid,
          syndications_pricing_reasoning: this.syndReasoning.value,
        };
      }
      this.updatePricingDetails(
        reasoningArr,
        'Updated Syndications Pricing Reasoning',
        'reasoning'
      );
    }
    this.isReasoningEditMode = !this.isReasoningEditMode;
  }

  toggleNonCRAEditMode() {
    if (
      this.isNonCRAEditMode &&
      this.nonCRAChanged &&
      this.hasPricingPermissions
    ) {
      let nonCRAArr = {};
      if (this.pricingDetails) {
        nonCRAArr = {
          id: this.pricingDetails.id,
          non_cra_execution: this.nonCRA.value,
        };
      } else {
        nonCRAArr = {
          project_id: this.projectid,
          non_cra_execution: this.nonCRA.value,
        };
      }
      this.updatePricingDetails(
        nonCRAArr,
        'Updated Non-CRA Execution',
        'nonCRA'
      );
    }
    this.isNonCRAEditMode = !this.isNonCRAEditMode;
  }

  updatePricingDetails(obj: any, message: string, type: string) {
    this.apollo
      .mutate({
        mutation: update_pricing_details,
        variables: obj,
      })
      .subscribe((response: any) => {
        // @ts-ignore
        this.pricingDetails = response.data['updatePricingDetails'];
        if (type === 'reasoning') {
          this.syndReasoning.setValue(
            this.pricingDetails.syndications_pricing_reasoning
          );
          this.syndReasoningChanged = false;
        } else if (type === 'questions') {
          this.syndQuestions.setValue(
            this.pricingDetails.syndications_questions
          );
          this.syndQuestionsChanged = false;
        } else if (type === 'nonCRA') {
          this.nonCRA.setValue(this.pricingDetails.non_cra_execution);
          this.nonCRAChanged = false;
        }
        this.openSnackBar(message);
      });
  }

  toggleEditMode() {
    if (this.isEditMode && this.hasPricingPermissions) {
      let dateArr = [];
      let dateDeleteArr = [];

      if (this.placementExpDate !== this.placementExpDateNew) {
        let dateObj = {};
        if (this.placementExpDateNew !== '') {
          if (
            this.placementExpDateObj &&
            this.placementExpDateObj.hasOwnProperty('id')
          ) {
            dateObj = {
              id: this.placementExpDateObj.id,
              date: moment.utc(this.placementExpDateNew).format('YYYY-MM-DD'),
            };
          } else {
            dateObj = {
              date: moment.utc(this.placementExpDateNew).format('YYYY-MM-DD'),
              date_type_id: this.placementExpDateID,
              date_class_id: this.actualDateClassID,
              project_id: this.projectid,
            };
          }
          dateArr.push(dateObj);
        } else {
          dateDeleteArr.push({ id: this.placementExpDateObj.id });
          this.placementExpDateObj = null;
        }
      }

      if (this.placementIssuedDate !== this.placementIssuedDateNew) {
        let dateObj = {};
        if (this.placementIssuedDateNew !== '') {
          if (
            this.placementIssuedDateObj &&
            this.placementIssuedDateObj.hasOwnProperty('id')
          ) {
            dateObj = {
              id: this.placementIssuedDateObj.id,
              date: moment
                .utc(this.placementIssuedDateNew)
                .format('YYYY-MM-DD'),
            };
          } else {
            dateObj = {
              date: moment
                .utc(this.placementIssuedDateNew)
                .format('YYYY-MM-DD'),
              date_type_id: this.placementIssuedDateID,
              date_class_id: this.actualDateClassID,
              project_id: this.projectid,
            };
          }
          dateArr.push(dateObj);
        } else {
          dateDeleteArr.push({ id: this.placementIssuedDateObj.id });
          this.placementIssuedDateObj = null;
        }
      }

      if (this.responseDate !== this.responseDateNew) {
        let dateObj = {};
        if (this.responseDateNew !== '') {
          if (
            this.responseDateObj &&
            this.responseDateObj.hasOwnProperty('id')
          ) {
            dateObj = {
              id: this.responseDateObj.id,
              date: moment.utc(this.responseDateNew).format('YYYY-MM-DD'),
            };
          } else {
            dateObj = {
              date: moment.utc(this.responseDateNew).format('YYYY-MM-DD'),
              date_type_id: this.responseToDevID,
              date_class_id: this.actualDateClassID,
              project_id: this.projectid,
            };
          }
          dateArr.push(dateObj);
        } else {
          dateDeleteArr.push({ id: this.responseDateObj.id });
          this.responseDateObj = null;
        }
      }

      let projDatesObj = {};
      if (dateArr.length > 0 && dateDeleteArr.length > 0) {
        projDatesObj = {
          upsert: dateArr,
          delete: dateDeleteArr,
        };
      } else if (dateArr.length > 0 && dateDeleteArr.length <= 0) {
        projDatesObj = {
          upsert: dateArr,
        };
      } else if (dateArr.length <= 0 && dateDeleteArr.length > 0) {
        projDatesObj = {
          delete: dateDeleteArr,
        };
      }

      if (
        projDatesObj.hasOwnProperty('upsert') ||
        projDatesObj.hasOwnProperty('delete')
      ) {
        this.apollo
          .mutate({
            mutation: update_project_dates,
            variables: {
              input: {
                id: this.projectid,
                projectDates: projDatesObj,
              },
            },
          })
          .subscribe((response: any) => {
            this.dates =
              response.data.updateProjectDates.projectDates[0].project.projectDates;
            this.formatDates();
            this.openSnackBar('Updated Pricing Details.');
          });
      }

      let partObj = null;
      if (
        (!this.syndLeadPart && this.syndLeadPartNew) ||
        this.syndLeadPart?.id !== !this.syndLeadPartNew?.id ||
        this.syndLeadPart?.person?.id !== this.syndLeadPartNew?.person?.id
      ) {
        if (this.syndLeadPartNew?.person?.id) {
          partObj = {
            id: this.projectid,
            participants: {
              upsert: [
                {
                  id: this.syndLeadPartNew.id,
                  role_id: this.syndLeadRole.id,
                  person_id: this.syndLeadPartNew.person.id,
                },
              ],
            },
          };
        } else if (this.syndLeadPart?.id) {
          partObj = {
            id: this.projectid,
            participants: {
              delete: [this.syndLeadPart.id],
            },
          };
        }
      }

      if (partObj) {
        this.apollo
          .mutate({
            mutation: update_project_participants,
            variables: {
              input: partObj,
            },
          })
          .subscribe((response: any) => {
            this.participants = response.data.updateParticipants.participants;
            this.participantsChange.emit(this.participants);
            this.formatSyndLeadPart();
            this.openSnackBar('Updated Pricing Details.');
          });
      }
    }
    this.isEditMode = !this.isEditMode;
  }

  changeDate(event: any, dateType: string) {
    if (dateType === 'placementExp') {
      this.placementExpDateNew = new Date(event.value).toISOString();
    } else if (dateType === 'placementIssued') {
      this.placementIssuedDateNew = new Date(event.value).toISOString();
    } else if (dateType === 'responseDate') {
      this.responseDateNew = new Date(event.value).toISOString();
    }
  }

  deleteDate(dateType: string) {
    if (dateType === 'placementExp') {
      this.placementExpDateNew = '';
    } else if (dateType === 'placementIssued') {
      this.placementIssuedDateNew = '';
    } else if (dateType === 'responseDate') {
      this.responseDateNew = '';
    }
  }

  searchParticipantText(role: any, searchText: any) {
    this.currentSearchResults = [];
    if (searchText.length < 1) {
      return;
    }
    this.loadingSearchResults = true;
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
    if (typeof searchText !== 'string') {
      return;
    }
    this.querySubscription = this.apollo
      .watchQuery<any>({
        query: participants_search_people,
        variables: {
          searchLike: '%' + searchText.trim() + '%',
          limit: 10,
          search: searchText.trim(),
          role: role.name,
        },
      })
      .valueChanges.subscribe(({ data }) => {
        this.loadingSearchResults = false;
        this.currentSearchResults = [];
        for (let i = 0; i < data.participant_orgs.data.length; i += 1) {
          let partRow: any = {
            active: true,
            person: null,
            participant: data.participant_orgs.data[i],
            role: role,
          };

          this.currentSearchResults.push(partRow);
        }
        for (let i = 0; i < data.participant_people.data.length; i += 1) {
          let partRow: any = {
            active: true,
            participant: data.participant_people.data[i],
            role: role,
          };
          this.currentSearchResults.push(partRow);
        }
      });
  }

  initializeAutocomplete() {
    this.currentSearchResults = [];
    if (this.typeSubscription) {
      this.typeSubscription.unsubscribe();
    }
    this.typeSubscription = this.searchParticipant.valueChanges
      .pipe(
        debounceTime(500), // Waiting for 500ms while you're typing
        distinctUntilChanged() // Prevents the emitting if the 'start' value and the 'end' value are the same
      )
      .subscribe((value: any) => {
        this.searchParticipantText(this.syndLeadRole, value);
      });
  }

  participantOptionText(opt: any) {
    return typeof opt === 'string' ? opt : '';
  }

  updateSyndLead(val: any) {
    this.syndLeadPartNew = {
      id: this.syndLeadPartNew?.id ? this.syndLeadPartNew.id : null,
      organization: null,
      person: val.participant,
      role: this.syndLeadRole,
    };
    this.currentSearchResults = [];
    this.searchParticipant.setValue(this.syndLeadPartNew.person.name);
  }

  deleteSyndLead() {
    this.syndLeadPartNew = {
      id: null,
      organization: null,
      person: null,
      role: this.syndLeadRole,
    };
    this.searchParticipant.setValue('');
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }
}
